import React, { useCallback, useRef, useState } from 'react';
import {
  createManualPoolMember,
  ManualPoolMemberApiError,
  type ManualPoolMemberResult,
} from '../../services/candidatePoolsApi';
import { looksLikeEmail } from '../../utils/looksLikeEmail';

const SENIORITY_OPTIONS = ['intern', 'junior', 'mid', 'senior', 'lead', 'principal'] as const;
const AVAILABILITY_OPTIONS = ['available', 'open', 'not_looking'] as const;

type FormState = {
  name: string;
  title: string;
  contact: string;
  linkedin_profile_url: string;
  primary_location: string;
  professional_summary: string;
  seniority_level: string;
  availability_status: string;
  years_experience_total: string;
  professional_interests: string;
  expertise: string;
};

const EMPTY_FORM: FormState = {
  name: '',
  title: '',
  contact: '',
  linkedin_profile_url: '',
  primary_location: '',
  professional_summary: '',
  seniority_level: '',
  availability_status: '',
  years_experience_total: '',
  professional_interests: '',
  expertise: '',
};

function buildPayload(form: FormState): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
  };
  if (form.title.trim()) payload.title = form.title.trim();
  if (form.contact.trim()) payload.contact = form.contact.trim();
  if (form.linkedin_profile_url.trim()) payload.linkedin_profile_url = form.linkedin_profile_url.trim();
  if (form.primary_location.trim()) payload.primary_location = form.primary_location.trim();
  if (form.professional_summary.trim()) payload.professional_summary = form.professional_summary.trim();
  if (form.seniority_level) {
    payload.seniority_level = form.seniority_level;
  }
  if (form.availability_status) {
    payload.availability_status = form.availability_status;
  }
  if (form.years_experience_total.trim()) {
    const n = parseInt(form.years_experience_total, 10);
    if (!Number.isNaN(n)) payload.years_experience_total = n;
  }
  if (form.professional_interests.trim()) {
    payload.professional_interests = form.professional_interests.trim();
  }
  if (form.expertise.trim()) payload.expertise = form.expertise.trim();
  return payload;
}

const inputClass =
  'w-full rounded-md bg-dark-bg border border-dark-border px-3 py-2 text-sm text-dark-text placeholder-dark-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-indigo-500';

export interface AddPoolMemberModalProps {
  poolId: number;
  poolName: string;
  onClose: () => void;
  onSuccess: (result: ManualPoolMemberResult) => void;
}

export const AddPoolMemberModal: React.FC<AddPoolMemberModalProps> = ({
  poolId,
  poolName,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const update = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldError(null);
    setServerError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFieldError({ field: 'name', message: 'Name is required' });
      nameRef.current?.focus();
      return;
    }
    if (form.contact.trim() && !looksLikeEmail(form.contact.trim())) {
      setFieldError({ field: 'contact', message: 'Enter a valid email address' });
      return;
    }

    setSaving(true);
    setServerError(null);
    try {
      const result = await createManualPoolMember(poolId, buildPayload(form));
      onSuccess(result);
    } catch (err: unknown) {
      if (err instanceof ManualPoolMemberApiError && err.field) {
        setFieldError({ field: err.field, message: err.message });
      } else {
        setServerError(err instanceof Error ? err.message : 'Failed to add person');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="pool-add-person-modal"
    >
      <div
        className="absolute inset-0 bg-black/60"
        aria-hidden="true"
        onClick={saving ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pool-add-person-title"
        className="relative w-full max-w-lg bg-dark-card border border-dark-border rounded-xl shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-dark-border px-6 py-4">
            <div>
            <h2 id="pool-add-person-title" className="text-lg font-bold text-dark-text">
              Add person to pool
            </h2>
            <p className="text-xs text-dark-text-secondary mt-1">
              Manual entry for <span className="text-indigo-300">{poolName}</span> — no CV import.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-dark-text-secondary hover:text-dark-text text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <form className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto" onSubmit={handleSubmit}>
          <Field
            label="Name"
            fieldId="pool-add-name"
            required
            error={fieldError?.field === 'name' ? fieldError.message : null}
          >
            <input
              ref={nameRef}
              id="pool-add-name"
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              className={inputClass}
              maxLength={255}
              disabled={saving}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Title" fieldId="pool-add-title">
              <input
                id="pool-add-title"
                type="text"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                className={inputClass}
                maxLength={500}
                disabled={saving}
              />
            </Field>
            <Field label="Location" fieldId="pool-add-location">
              <input
                id="pool-add-location"
                type="text"
                value={form.primary_location}
                onChange={(e) => update('primary_location', e.target.value)}
                className={inputClass}
                maxLength={200}
                disabled={saving}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Email"
              fieldId="pool-add-contact"
              error={fieldError?.field === 'contact' ? fieldError.message : null}
            >
              <input
                id="pool-add-contact"
                type="email"
                value={form.contact}
                onChange={(e) => update('contact', e.target.value)}
                className={inputClass}
                maxLength={500}
                disabled={saving}
              />
            </Field>
            <Field label="LinkedIn profile URL" fieldId="pool-add-linkedin">
              <input
                id="pool-add-linkedin"
                type="url"
                value={form.linkedin_profile_url}
                onChange={(e) => update('linkedin_profile_url', e.target.value)}
                className={inputClass}
                maxLength={2048}
                placeholder="https://linkedin.com/in/..."
                disabled={saving}
              />
            </Field>
          </div>

          <Field label="Professional summary" fieldId="pool-add-summary">
            <textarea
              id="pool-add-summary"
              value={form.professional_summary}
              onChange={(e) => update('professional_summary', e.target.value)}
              rows={3}
              className={`${inputClass} resize-y`}
              maxLength={4000}
              disabled={saving}
            />
          </Field>

          <Field label="Skills / expertise (free text)" fieldId="pool-add-expertise">
            <textarea
              id="pool-add-expertise"
              value={form.expertise}
              onChange={(e) => update('expertise', e.target.value)}
              rows={2}
              className={`${inputClass} resize-y`}
              maxLength={8000}
              disabled={saving}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Seniority" fieldId="pool-add-seniority">
              <select
                id="pool-add-seniority"
                value={form.seniority_level}
                onChange={(e) => update('seniority_level', e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                <option value="">—</option>
                {SENIORITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Availability" fieldId="pool-add-availability">
              <select
                id="pool-add-availability"
                value={form.availability_status}
                onChange={(e) => update('availability_status', e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                <option value="">—</option>
                {AVAILABILITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Years of experience" fieldId="pool-add-years">
              <input
                id="pool-add-years"
                type="number"
                min={0}
                max={80}
                value={form.years_experience_total}
                onChange={(e) => update('years_experience_total', e.target.value)}
                className={inputClass}
                disabled={saving}
              />
            </Field>
          </div>

          {serverError ? (
            <p className="text-sm text-red-400" role="alert" data-testid="pool-add-person-error">
              {serverError}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2 border-t border-dark-border">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-dark-border text-sm text-dark-text-secondary hover:text-dark-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim()}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50"
              data-testid="pool-add-person-submit"
            >
              {saving ? 'Adding…' : 'Add to pool'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  fieldId: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
}> = ({ label, fieldId, required, error, children }) => (
  <div>
    <label htmlFor={fieldId} className="block text-xs font-medium text-dark-text-secondary mb-1.5">
      {label}
      {required ? ' *' : ''}
    </label>
    {children}
    {error ? (
      <p className="text-xs text-red-400 mt-1" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);
